import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import useTextToSpeech from "./Hooks/useTextToSpeech";
import { restServiceGet } from "./utils/rest";
import { geolocationService } from "./services";

import { Skeleton } from "@mui/material";
import { ReactComponent as Volume } from "../src/components/assets/volume.svg";
import "./Pid.scss";

const TEST_DATA = {
  stops: [
    {
      stopName: "St Peter's Square",
      stopTime: new Date(new Date().setHours(17, 0, 0, 0)),
      railReplacement: "16:04 Deansgate Castlefield",
      information:
        "Possible delays on the road network due to rail cancellations.",
    },
    {
      stopName: "St Paul's Cathedral",
      stopTime: new Date(new Date().setHours(17, 1, 0, 0)),
      railReplacement: "16:14 St Paul's",
      information: "Minor delays expected.",
    },
    {
      stopName: "Tower of London",
      stopTime: new Date(new Date().setHours(17, 2, 0, 0)),
      railReplacement: "16:24 Tower Hill",
      information: "No significant delays.",
    },
    {
      stopName: "London Bridge",
      stopTime: new Date(new Date().setHours(17, 3, 0, 0)),
      railReplacement: "16:34 London Bridge",
      information: "Minor delays expected.",
    },
    {
      stopName: "Canary Wharf",
      stopTime: new Date(new Date().setHours(17, 4, 0, 0)),
      railReplacement: "16:44 Canary Wharf",
      information: "No significant delays.",
    },
    {
      stopName: "Stratford",
      stopTime: new Date(new Date().setHours(17, 5, 0, 0)),
      railReplacement: "16:54 Stratford",
      information: "Minor delays expected.",
    },
    {
      stopName: "Westfield Stratford City",
      stopTime: new Date(new Date().setHours(17, 6, 0, 0)),
      railReplacement: "16:59 Westfield",
      information: "No significant delays.",
    },
    {
      stopName: "Olympic Park",
      stopTime: new Date(new Date().setHours(17, 7, 0, 0)),
      railReplacement: "17:04 Olympic Park",
      information: "Minor delays expected.",
    },
  ],
};

const FIVE_MINUTES_MS = 5 * 60 * 1000;

const ARRIVING_ANNOUNCEMENT_LEAD_TIME = 30 * 1000; // 30 seconds
const ARRIVING_ANNOUNCEMENT_LEAD_DISTANCE = 1000; // 1KM
const HAS_ARRIVED_DISTANCE_THRESHOLD = 100; // 100 Meters
const LIST_ELEMENT_HEIGHT = 256;
const SPEECH_DURATION_PADDING = 2;

const Pid = ({ apiToken }) => {
  const [devMode, setDevMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [time, setTime] = useState(() => new Date());
  const [nextStopIndex, setNextStopIndex] = useState(0);
  const [service, setService] = useState(TEST_DATA);
  const [announcement, setAnnouncement] = useState(" ");
  const [announcementDuration, setAnnouncementDuration] = useState(15); // Seconds!
  const [isArriving, setIsArriving] = useState(false);
  const [position, setPosition] = useState(null);

  const { speak, isSpeaking } = useTextToSpeech();

  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const tripId = query.get("tripId");
  const tripDate = query.get("tripDate");
  const vehicleRegistration = query.get("vehicleRegistration");
  const startingDateTime = query.get("startingDateTime");
  const isDevMode = query.get("devMode") === "true";
  const arrivingAnnouncementLeadTime = query.get("arrivingAnnouncementLeadTime")
    ? parseInt(query.get("arrivingAnnouncementLeadTime"), 10) * 1000
    : ARRIVING_ANNOUNCEMENT_LEAD_TIME;
  const arrivingAnnouncementLeadDistance = query.get(
    "arrivingAnnouncementLeadDistance"
  )
    ? parseInt(query.get("arrivingAnnouncementLeadDistance"), 10)
    : ARRIVING_ANNOUNCEMENT_LEAD_DISTANCE;
  const hasArrivedDistanceThreshhold = query.get("hasArrivedDistanceThreshold")
    ? parseInt(query.get("hasArrivedDistanceThreshold"), 10)
    : HAS_ARRIVED_DISTANCE_THRESHOLD;

  // Initial load
  useEffect(() => {
    async function fetchInitialData() {
      try {
        if (!tripId) {
          setError(new Error("Could not find specified service!"));
          return;
        }

        const pidScreenResponse = await restServiceGet(
          apiToken,
          "getPidScreenData/" + tripId + "/" + tripDate
        );

        const stops = pidScreenResponse.data.map((d) => ({
          ...d,
          stopTime: new Date(d.stopTime),
        }));

        setService({
          stops: stops,
        });
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setError(error.response.data);
      }
    }

    fetchInitialData();
    setDevMode(isDevMode);
  }, []);

  // Fetch position data every 30 seconds
  useEffect(() => {
    let isMounted = true;

    async function fetchPositionData() {
      if (!vehicleRegistration) return;
      try {
        const positionResponse = await restServiceGet(
          apiToken,
          "getDevicePosition/" + vehicleRegistration
        );
        if (isMounted && positionResponse.data) {
          setPosition(
            positionResponse.data.locations[
              positionResponse.data.locations.length - 1
            ]
          );
        } else {
          setPosition(fallbackGetPosition());
        }
      } catch (error) {
        setPosition(fallbackGetPosition());
        console.error("Error fetching position data:", error);
        setError(error);
      }
    }

    fetchPositionData();
    setDevMode(isDevMode);

    const intervalId = setInterval(fetchPositionData, 30_000);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [tripId, apiToken]);

  useEffect(() => {
    const startMs = startingDateTime
      ? new Date(startingDateTime).getTime()
      : null;
    const baseNow = Date.now();

    setTime(
      startMs !== null ? new Date(startMs + (Date.now() - baseNow)) : new Date()
    );

    const id = setInterval(() => {
      setTime(
        startMs !== null
          ? new Date(startMs + (Date.now() - baseNow))
          : new Date()
      );
    }, 1000);

    return () => clearInterval(id);
  }, [startingDateTime]);

  useEffect(() => {
    const timeUntilNextStop = service.stops[nextStopIndex].stopTime - time;

    const audioMessage = service.stops[nextStopIndex].audioMessage;
    const audioUrl = service.stops[nextStopIndex].audioUrl;

    determinePidState(timeUntilNextStop, position, audioMessage, audioUrl);
  }, [time]);

  const fallbackGetPosition = () => {
    return geolocationService.getCurrentPosition();
  };

  const determinePidState = (
    timeUntilNextStop,
    positionData,
    audioMessage,
    audioUrl
  ) => {
    if (!service || nextStopIndex >= service.stops.length) {
      return;
    }

    const hasPositionData = !!positionData;

    const nextStopPosition = {
      latitude: parseFloat(service.stops[nextStopIndex].latitude),
      longitude: parseFloat(service.stops[nextStopIndex].longitude),
    };

    // 1 - If the position is within ARRIVING_ANNOUNCEMENT_LEAD_DISTANCE, trigger arriving logic.
    if (
      hasPositionData &&
      distanceBetweenPositions(positionData, nextStopPosition) <=
        arrivingAnnouncementLeadDistance &&
      !isArriving
    ) {
      toggleArriving(audioMessage, audioUrl);
      return;
    }

    // 2 - If the time to the next stop is less than arrivingAnnouncementLeadTime, trigger arriving logic.
    if (timeUntilNextStop <= arrivingAnnouncementLeadTime && !isArriving) {
      toggleArriving(audioMessage, audioUrl);
      return;
    }

    // 3 - If the position is within HAS_ARRIVED_DISTANCE_THRESHOLD, trigger next stop logic.
    if (
      hasPositionData &&
      distanceBetweenPositions(positionData, nextStopPosition) <=
        hasArrivedDistanceThreshhold &&
      isArriving
    ) {
      if (nextStopIndex < service.stops.length - 1) {
        toggleNextStop();
      }
      return;
    }

    // 4 - If the time to the next stop is less than or equal to 0, trigger next stop logic.
    if (timeUntilNextStop <= 0 && isArriving) {
      if (nextStopIndex < service.stops.length - 1) {
        toggleNextStop();
      }
      return;
    }
  };

  const distanceBetweenPositions = (pos1, pos2) => {
    const toRad = (value) => (value * Math.PI) / 180;

    const R = 6371e3; // metres
    const φ1 = toRad(pos1.latitude);
    const φ2 = toRad(pos2.latitude);
    const Δφ = toRad(pos2.latitude - pos1.latitude);
    const Δλ = toRad(pos2.longitude - pos1.longitude);

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    const d = R * c;
    return d;
  };

  const toggleArriving = (message, audioUrl) => {
    // TODO: Re-enable audio message playback once we have a single browser interaction.
    // if (message && audioUrl) {
    //   setAnnouncement(message);
    //   const audio = new Audio(audioUrl);
    //   audio.play();
    //   const duration = Math.ceil(audio.duration);
    //   setAnnouncementDuration(duration + SPEECH_DURATION_PADDING);
    //   return;
    // }

    setIsArriving(true);
    const arrivalAnnouncement = `We are now arriving at ${service.stops[nextStopIndex].stopName}. ${service.stops[nextStopIndex].information} `;
    setAnnouncement(arrivalAnnouncement);
    const duration = speak(arrivalAnnouncement);
    setAnnouncementDuration(duration + SPEECH_DURATION_PADDING);
  };

  const toggleNextStop = () => {
    setNextStopIndex((prev) => prev + 1);
    setIsArriving(false);
  };

  const loadTestData = () => {
    setService(TEST_DATA);
    setLoading(false);
    setNextStopIndex(0);
    setIsArriving(false);
  };

  const formatTime = (date) =>
    date.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

  const renderStopSVGIndicator = (stopIndex, totalStops) => {
    const isLastStop = stopIndex === totalStops - 1;
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 128 256"
        style={{
          width: "128px",
          height: LIST_ELEMENT_HEIGHT + "px",
        }}
        preserveAspectRatio="xMidYMid meet"
      >
        <circle
          cx="42"
          cy="42"
          r="42"
          fill="#FDF45B"
        />
        {!isLastStop && (
          <line
            x1="42"
            y1="51"
            x2="42"
            y2="256"
            stroke="#FDF45B"
            strokeWidth="16"
            strokeDasharray="24, 19"
            strokeLinecap="round"
          />
        )}
      </svg>
    );
  };

  const renderMultiLineSkeleton = (lines, lineHeight) => {
    return (
      <div className="skeleton-container-small">
        {Array.from({ length: lines }, (_, index) => (
          <Skeleton
            variant="rounded"
            sx={{
              bgcolor: "grey",
              height: lineHeight,
              marginBottom: index !== lines - 1 ? "8px" : "0px",
            }}
          />
        ))}
      </div>
    );
  };

  const renderDevButtons = () => {
    return (
      <div className="dev-buttons">
        <span className="dev-info dev-info--current">
          Current position:{" "}
          {position ? `${position.latitude}, ${position.longitude}` : "N/A"}
        </span>
        <span className="dev-info dev-info--next-stop">
          Next stop date-time: {nextStop.stopTime.toString()}
        </span>
        <button
          disabled={nextStopIndex === 0}
          onClick={() => {
            setNextStopIndex((prev) => Math.max(0, prev - 1));
            setIsArriving(false);
          }}
        >
          Force Previous Stop
        </button>
        <button
          disabled={nextStopIndex === service.stops.length - 1}
          onClick={() => {
            setNextStopIndex((prev) =>
              Math.min(service.stops.length - 1, prev + 1)
            );
            setIsArriving(false);
          }}
        >
          Force Next Stop
        </button>
        <button
          disabled={isSpeaking}
          onClick={() => {
            setAnnouncement(announcement + "This is a forced announcement.");
            const duration = speak(announcement);
            setAnnouncementDuration(duration + 2);
          }}
        >
          Force Announcement
        </button>
        <button
          className="dev-button dev-button--load"
          onClick={() => {
            loadTestData();
          }}
        >
          Load Test Data
        </button>
        <button
          className="dev-button dev-button--unload"
          onClick={() => {
            setService(null);
          }}
        >
          Unload Test Data
        </button>
      </div>
    );
  };

  if (error) {
    return (
      <div className="pid-container">
        <span className="error-message">Error: {error}</span>
      </div>
    );
  }

  // If the service is finished more than 24 hours ago, show a nice service finished message
  const finalStopTime = service.stops[service.stops.length - 1].stopTime;
  if (time - finalStopTime > FIVE_MINUTES_MS) {
    return (
      <div className="pid-container">
        <div className="title-container">
          <span className="flash-white">Service Finished</span>
          <span className="generic-padding-right">
            {time.toLocaleTimeString("en-GB", { hour12: false })}
            <br />
          </span>
          <div className="main-title">This service has finished.</div>
        </div>
      </div>
    );
  }

  const nextStop = service?.stops[nextStopIndex];
  const stopListInitialOffset = 54;
  const nextStopsPositionOffset = (nextStopIndex + 1) * -LIST_ELEMENT_HEIGHT;
  const isFinalStop = nextStopIndex === service.stops.length - 1;

  return (
    <div className="pid-container">
      <div className="title-container">
        {isArriving ? (
          <span className="flash-white">Now Arriving at</span>
        ) : (
          <>
            <span className="generic-padding">Next Stop</span>
            <b
              style={{ paddingLeft: "24px" }}
              className="generic-padding"
            >
              {formatTime(nextStop.stopTime)}
            </b>
          </>
        )}
        <span className="generic-padding-right">
          {time.toLocaleTimeString("en-GB", { hour12: false })}
          <br />
          {devMode ? renderDevButtons() : null}
        </span>
        {!loading && nextStop ? (
          <div className="main-title">{nextStop.stopName}</div>
        ) : (
          <div className="skeleton-container-large">
            <Skeleton
              variant="rounded"
              sx={{
                bgcolor: "grey",
                height: "clamp(60px, 12vh, 92px)",
              }}
            />
          </div>
        )}
      </div>
      <div className="main-container">
        <div className="left">
          {loading ? (
            <>
              {[
                [1, 2, 3].map((item) => (
                  <div
                    style={{ marginBottom: "40px" }}
                    key={item}
                  >
                    {renderMultiLineSkeleton(2, 84)}
                  </div>
                )),
              ]}
            </>
          ) : (
            <div style={{ marginTop: stopListInitialOffset }}>
              <div
                className="stop-list-container"
                style={{
                  transform: `translateY(${nextStopsPositionOffset}px)`,
                }}
              >
                {service.stops.map((stop, index) => (
                  <div
                    key={index}
                    className="stop-item"
                  >
                    {renderStopSVGIndicator(index, service.stops.length)}
                    <div className="stop-text">
                      <b>{stop.stopName}</b>
                      <div>Arrives {formatTime(stop.stopTime)}</div>
                    </div>
                  </div>
                ))}
                {isFinalStop ? (
                  <span
                    style={{ color: "#FDF45B" }}
                    className="flash-white"
                  >
                    This is the final stop of the service. Please disembark.
                  </span>
                ) : null}
              </div>
            </div>
          )}
        </div>
        <div className="right">
          <div className="right-top">
            <div className="subtitle">Rail Replacement</div>
            {!loading ? (
              <div className="generic-padding">{nextStop.railReplacement}</div>
            ) : (
              renderMultiLineSkeleton(2, 96)
            )}
          </div>
          <div className="right-bottom">
            <div className="subtitle">
              <span
                role="img"
                aria-label="information"
                className="information-icon flash-white"
              >
                ⓘ
              </span>{" "}
              Information
            </div>
            {!loading ? (
              <div className="generic-padding">{nextStop.information}</div>
            ) : (
              renderMultiLineSkeleton(4, 48)
            )}
          </div>
        </div>
      </div>
      <div className="announcement-container">
        <Volume className={`volume-icon ${isSpeaking ? "flash-white" : ""}`} />
        {!loading ? (
          <div className="announcement">
            <span
              key={announcement}
              style={{
                animation: `scroll-once ${announcementDuration}s linear 1 forwards`,
              }}
            >
              {announcement}
            </span>
          </div>
        ) : (
          <div className="skeleton-container-large">
            <Skeleton
              variant="rounded"
              sx={{
                bgcolor: "grey",
                height: "clamp(60px, 4vh, 92px)",
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Pid;
